<script setup>
import { ref, nextTick, watch, onBeforeUnmount, useTemplateRef } from "vue";
import { createPopper } from "@popperjs/core";
import { useStore } from "vuex";

import UserSettings from "@/components/shared/profile/UserSettings.vue";
import { useTheme } from "@/composables/useTheme";

const emit = defineEmits(["menu-open-change"]);

const store = useStore();
const { isCustomTheme, themeOptions, selectedThemeOption, setThemePreference } = useTheme();

const isOpen = ref(false);
const isSettingsModalOn = ref(false);
const isThemeSubmenuOpen = ref(false);
let popperInstance = null;
let themeSubmenuCloseTimer = null;

function openThemeSubmenu() {
  if (themeSubmenuCloseTimer) {
    clearTimeout(themeSubmenuCloseTimer);
    themeSubmenuCloseTimer = null;
  }
  isThemeSubmenuOpen.value = true;
}

function scheduleThemeSubmenuClose() {
  if (themeSubmenuCloseTimer) clearTimeout(themeSubmenuCloseTimer);
  themeSubmenuCloseTimer = setTimeout(() => {
    isThemeSubmenuOpen.value = false;
    themeSubmenuCloseTimer = null;
  }, 500);
}

const trigger = useTemplateRef("trigger");
const panel = useTemplateRef("panel");

function toggle() {
  isOpen.value = !isOpen.value;
}

function close() {
  isOpen.value = false;
}

function handleClickOutside(e) {
  if (trigger.value && !trigger.value.contains(e.target) && (!panel.value || !panel.value.contains(e.target))) {
    close();
  }
}

function initPopper() {
  destroyPopper();

  if (trigger.value && panel.value) {
    popperInstance = createPopper(trigger.value, panel.value, {
      placement: "top-start",
      strategy: "fixed",
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, 8],
          },
        },
        {
          name: "preventOverflow",
          options: {
            padding: 8,
            boundary: "viewport",
          },
        },
      ],
    });
  }
}

function destroyPopper() {
  if (popperInstance) {
    popperInstance.destroy();
    popperInstance = null;
  }
}

function logout() {
  store.dispatch("logout");
}

function handleThemeChange(option) {
  setThemePreference(option.value);
  isThemeSubmenuOpen.value = false;
  close();
}

function isThemeSelected(option) {
  return selectedThemeOption.value?.value === option.value;
}

function openSettings() {
  isSettingsModalOn.value = true;
  close();
}

watch(isOpen, (val) => {
  emit("menu-open-change", val);
  if (val) {
    nextTick(() => {
      initPopper();
      document.addEventListener("mousedown", handleClickOutside);
    });
  } else {
    if (themeSubmenuCloseTimer) {
      clearTimeout(themeSubmenuCloseTimer);
      themeSubmenuCloseTimer = null;
    }
    isThemeSubmenuOpen.value = false;
    document.removeEventListener("mousedown", handleClickOutside);
    destroyPopper();
  }
});

onBeforeUnmount(() => {
  if (themeSubmenuCloseTimer) {
    clearTimeout(themeSubmenuCloseTimer);
    themeSubmenuCloseTimer = null;
  }
  document.removeEventListener("mousedown", handleClickOutside);
  destroyPopper();
});
</script>

<template>
  <div class="sidebar-user-menu">
    <div
      ref="trigger"
      class="sidebar-user-menu-trigger"
      role="button"
      tabindex="0"
      :aria-expanded="isOpen"
      aria-haspopup="menu"
      @click="toggle"
      @keydown.enter.prevent="toggle"
      @keydown.space.prevent="toggle"
      @keydown.escape="close"
    >
      <slot />
    </div>

    <Teleport to="body">
      <div v-show="isOpen" ref="panel" class="sidebar-user-menu-panel">
        <ul class="sidebar-user-menu-list" role="menu">
          <li class="sidebar-user-menu-item" role="menuitem" tabindex="-1" @click="openSettings">
            <i class="bi-gear" />
            <span>{{ $t("common.label.settings") }}</span>
          </li>

          <li class="sidebar-user-menu-divider" />

          <li
            v-if="!isCustomTheme"
            class="sidebar-user-menu-item sidebar-user-menu-theme-trigger"
            role="menuitem"
            tabindex="-1"
            aria-haspopup="menu"
            :aria-expanded="isThemeSubmenuOpen"
            @mouseenter="openThemeSubmenu"
            @mouseleave="scheduleThemeSubmenuClose"
            @click.stop="isThemeSubmenuOpen = !isThemeSubmenuOpen"
          >
            <i :class="selectedThemeOption.icon" />
            <span>{{ selectedThemeOption.text }}</span>
            <i class="bi-chevron-right ml-auto text-xs" />

            <div v-show="isThemeSubmenuOpen" class="sidebar-user-menu-theme-submenu">
              <ul role="menu">
                <li
                  v-for="option in themeOptions"
                  :key="option.value"
                  class="sidebar-user-menu-item"
                  role="menuitem"
                  tabindex="-1"
                  @click.stop="handleThemeChange(option)"
                >
                  <i :class="option.icon" />
                  <span>{{ option.text }}</span>
                  <i v-if="isThemeSelected(option)" class="bi-check ml-auto text-primary" />
                </li>
              </ul>
            </div>
          </li>

          <li v-if="!isCustomTheme" class="sidebar-user-menu-divider" />

          <li class="sidebar-user-menu-item" role="menuitem" tabindex="-1" @click="logout">
            <i class="bi-box-arrow-right" />
            <span>{{ $t("common.action.logout") }}</span>
          </li>
        </ul>
      </div>
    </Teleport>

    <UserSettings :is-modal-on="isSettingsModalOn" @close="isSettingsModalOn = false" />
  </div>
</template>

<style lang="scss" scoped>
.sidebar-user-menu {
  @apply w-full;
}

.sidebar-user-menu-trigger {
  @apply w-full cursor-pointer;
}
</style>

<style lang="scss">
.sidebar-user-menu-panel {
  @apply z-[9999] min-w-[200px];
}

.sidebar-user-menu-list {
  @apply bg-base-100 border border-accent rounded-lg shadow-lg p-1 list-none m-0;
}

.sidebar-user-menu-item {
  @apply flex items-center gap-3 py-2 px-3 rounded-md cursor-pointer text-sm relative whitespace-nowrap;
  color: oklch(var(--bc));

  &:hover {
    background-color: color-mix(in oklch, oklch(var(--p)) 10%, transparent);
  }

  i {
    @apply text-base w-5 text-center shrink-0;
  }
}

.sidebar-user-menu-divider {
  @apply h-px mx-2 my-1 list-none;
  background-color: oklch(var(--bc) / 0.1);
}

.sidebar-user-menu-theme-trigger {
  @apply relative;

  .sidebar-user-menu-theme-submenu {
    @apply absolute left-full bottom-0 ml-1;

    // invisible bridge covering the gap between trigger and submenu,
    // prevents mouseleave when cursor moves diagonally toward submenu items
    &::before {
      content: "";
      position: absolute;
      right: 100%;
      top: -8px;
      bottom: -8px;
      width: 16px;
    }

    ul {
      @apply bg-base-100 border border-accent rounded-lg shadow-lg p-1 list-none m-0 min-w-[160px];
    }
  }
}
</style>
